import { useState, useRef, useCallback, useEffect } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { uploadService } from '@/services/uploadService';
import type { ProcessingStatus } from '@/services/uploadService';
import { closeUploadModal, showToast, setUploadInProgress } from '@/features/ui/uiSlice';
import Button from '@/components/ui/Button/Button';
import styles from './UploadModal.module.css';

// ─── Types ─────────────────────────────────────────────────────────────────

type UiView = 'drop' | 'meta' | 'queue';
type ContentType = 'media' | 'document';
type FileStatus = 'pending' | 'uploading' | 'completing' | 'processing' | 'done' | 'error';

interface FileEntry {
  id: string;
  file: File;
  title: string;
  contentType: ContentType;
  tags: string;
  description: string;
  // runtime state
  status: FileStatus;
  progress: number;
  chunksUploaded: number;
  totalChunks: number;
  processingStatus: ProcessingStatus;
  error: string | null;
}

// ─── Constants ─────────────────────────────────────────────────────────────

const FILE_EXTENSIONS: Record<string, string> = {
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
};

const VIEW_STEPS = ['Select', 'Details', 'Processing'];
const MAX_CHUNK_RETRIES = 3;
const CHUNK_RETRY_DELAY_MS = 10000;
const MAX_INITIATE_RETRIES = 3;
const INITIATE_RETRY_DELAY_MS = 10000;

const PROCESSING_LABELS: Record<NonNullable<ProcessingStatus>, string> = {
  pending_cu:    'Queued for AI...',
  cu_processing: 'AI analysing...',
  cu_done:       'Building index...',
  indexed:       'Indexed!',
  error:         'Failed',
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function processingToProgress(s: ProcessingStatus): number {
  switch (s) {
    case 'pending_cu':    return 74;
    case 'cu_processing': return 82;
    case 'cu_done':       return 94;
    case 'indexed':       return 100;
    default:              return 71;
  }
}

function getContentType(file: File): ContentType {
  return file.type.startsWith('video/') ? 'media' : 'document';
}

const VIDEO_EXTENSIONS = ['mp4', 'mov'];

function isVideoFile(file: File): boolean {
  if (file.type.startsWith('video/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return !!ext && VIDEO_EXTENSIONS.includes(ext);
}

function getExt(file: File): string {
  return FILE_EXTENSIONS[file.type] ?? file.name.split('.').pop()?.toUpperCase() ?? 'FILE';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

// Resolves after `ms`, or rejects with an AbortError if `signal` aborts first.
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    if (signal) {
      if (signal.aborted) onAbort();
      else signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

function makeFileEntry(file: File): FileEntry {
  return {
    id:               uid(),
    file,
    title:            file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '),
    contentType:      getContentType(file),
    tags:             '',
    description:      '',
    status:           'pending',
    progress:         0,
    chunksUploaded:   0,
    totalChunks:      0,
    processingStatus: null,
    error:            null,
  };
}

function splitFileName(name: string): [string, string] {
  const idx = name.lastIndexOf('.');
  if (idx <= 0) return [name, ''];
  return [name.slice(0, idx), name.slice(idx)];
}

// Appends " (1)", " (2)", ... before the extension until the name is unique.
function uniqueFileName(name: string, existing: Set<string>): string {
  if (!existing.has(name.toLowerCase())) return name;
  const [base, ext] = splitFileName(name);
  let i = 1;
  let candidate = `${base} (${i})${ext}`;
  while (existing.has(candidate.toLowerCase())) {
    i++;
    candidate = `${base} (${i})${ext}`;
  }
  return candidate;
}

function renameFile(file: File, newName: string): File {
  return new File([file], newName, { type: file.type, lastModified: file.lastModified });
}

interface DuplicateConflict {
  id: string;
  file: File;
}

// ─── Component ─────────────────────────────────────────────────────────────

export default function UploadModal() {
  const dispatch = useAppDispatch();

  const [view, setView] = useState<UiView>('drop');
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [duplicateQueue, setDuplicateQueue] = useState<DuplicateConflict[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRefs    = useRef<Map<string, AbortController>>(new Map());
  const pollRefs     = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // prevents double-triggering when the queue effect fires multiple times
  const startedIds   = useRef<Set<string>>(new Set());

  // ── Patch a single entry ────────────────────────────────────────────────
  const patchEntry = useCallback((id: string, patch: Partial<FileEntry>) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  }, []);

  // ── Cleanup on unmount ──────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      abortRefs.current.forEach(c => c.abort());
      pollRefs.current.forEach(t => clearTimeout(t));
    };
  }, []);

  // ── Lock background scroll while the modal is open ──────────────────────
  useEffect(() => {
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // ── Close handler ───────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    abortRefs.current.forEach(c => c.abort());
    pollRefs.current.forEach(t => clearTimeout(t));
    dispatch(closeUploadModal());
  }, [dispatch]);

  // ── ESC closes (only outside the active queue) ──────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && view !== 'queue') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose, view]);

  // ── Warn before unload while upload / AI analysis is in progress ─────────
  useEffect(() => {
    if (view !== 'queue') return;
    const isActive = entries.some(
      e => e.status === 'uploading' || e.status === 'completing' || e.status === 'processing',
    );
    if (!isActive) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers show their own message; setting returnValue keeps compat
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [view, entries]);

  // ── Auto-close when all entries are removed (manual cancel) ──────────────
  useEffect(() => {
    if (view !== 'queue' || entries.length > 0) return;
    dispatch(setUploadInProgress(false));
    dispatch(closeUploadModal());
  }, [view, entries, dispatch]);

  // ── Auto-close when every file is done / errored ────────────────────────
  useEffect(() => {
    if (view !== 'queue' || entries.length === 0) return;
    const allDone = entries.every(e => e.status === 'done' || e.status === 'error');
    if (!allDone) return;
    const doneCount = entries.filter(e => e.status === 'done').length;
    const total = entries.length;
    dispatch(setUploadInProgress(false));
    // If any file errored, leave the modal open so the user can read the
    // error message instead of auto-dismissing it.
    if (doneCount < total) return;
    const t = setTimeout(() => {
      dispatch(closeUploadModal());
      if (doneCount === 0) {
        dispatch(
          showToast({
            title: 'Upload Failed',
            message: `<b style="color:var(--color-text)">${total}</b> file${total !== 1 ? 's' : ''} failed to upload.`,
          }),
        );
      } else if (doneCount < total) {
        dispatch(
          showToast({
            title: 'Upload Partially Complete',
            message: `<b style="color:var(--color-text)">${doneCount} of ${total}</b> file${total !== 1 ? 's' : ''} indexed and searchable in Smart Hub. ${total - doneCount} failed.`,
          }),
        );
      } else {
        dispatch(
          showToast({
            title: 'Upload Complete',
            message: `<b style="color:var(--color-text)">${doneCount} of ${total}</b> file${total !== 1 ? 's' : ''} indexed and searchable in Smart Hub.`,
          }),
        );
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [entries, view, dispatch]);

  // ── Real API polling ────────────────────────────────────────────────────
  const pollStatus = useCallback((id: string, sessionId: string) => {
    const tick = async () => {
      try {
        const res = await uploadService.getStatus(sessionId);
        const s = res.processing_status;
        patchEntry(id, {
          processingStatus: s,
          progress: processingToProgress(s),
          status:   s === 'indexed' ? 'done' : s === 'error' ? 'error' : 'processing',
          error:    s === 'error' ? (res.error_message ?? 'Processing failed') : null,
        });
        if (s === 'indexed' || s === 'error') return;
        pollRefs.current.set(`api-${id}`, setTimeout(tick, 5000));
      } catch {
        // API unreachable mid-poll — surface as an error rather than faking success
        patchEntry(id, { status: 'error', error: 'Lost connection while processing. Please try again.' });
      }
    };
    pollRefs.current.set(`api-${id}`, setTimeout(tick, 3000));
  }, [patchEntry]);

  // ── Upload a single file (no recursion — queue effect drives sequencing) ─
  const doUpload = useCallback(async (entry: FileEntry) => {
    if (startedIds.current.has(entry.id)) return;
    startedIds.current.add(entry.id);

    const { id } = entry;
    const controller = new AbortController();
    abortRefs.current.set(id, controller);

    patchEntry(id, { status: 'uploading', progress: 0, chunksUploaded: 0, error: null });

    try {
      // 1. Initiate session ─────────────────────────────────────────────────
      let init: Awaited<ReturnType<typeof uploadService.initiate>>;
      for (let attempt = 1; ; attempt++) {
        try {
          init = await uploadService.initiate({
            file_name:       entry.file.name,
            file_size_bytes: entry.file.size,
            mime_type:       entry.file.type || 'application/octet-stream',
          });
          break;
        } catch (err: unknown) {
          const name = (err as { name?: string }).name;
          if (name === 'AbortError' || name === 'CanceledError') return;
          if (attempt >= MAX_INITIATE_RETRIES) {
            patchEntry(id, {
              status: 'error',
              progress: 0,
              error: 'Could not start upload. Please check your internet connection and try again.',
            });
            return;
          }
          console.warn(`[UploadModal] initiate failed (attempt ${attempt}/${MAX_INITIATE_RETRIES}), retrying`, err);
          await delay(INITIATE_RETRY_DELAY_MS, controller.signal);
        }
      }
      const sessionId = init.session_id;
      const chunkSize = init.chunk_size_bytes;
      const numChunks = init.total_chunks;
      console.log(`[UploadModal] session ${sessionId}: ${numChunks} chunk(s) x ${chunkSize} B`);

      patchEntry(id, { totalChunks: numChunks });

      // 2. Upload chunks ─────────────────────────────────────────────────────
      for (let i = 0; i < numChunks; i++) {
        if (controller.signal.aborted) return;
        const chunk = entry.file.slice(i * chunkSize, (i + 1) * chunkSize);

        for (let attempt = 1; ; attempt++) {
          try {
            await uploadService.uploadChunk(sessionId, i, chunk, controller.signal);
            break;
          } catch (err: unknown) {
            const name = (err as { name?: string }).name;
            if (name === 'AbortError' || name === 'CanceledError') throw err;
            if (attempt >= MAX_CHUNK_RETRIES) throw err;
            console.warn(`[UploadModal] chunk ${i} failed (attempt ${attempt}/${MAX_CHUNK_RETRIES}), retrying`, err);
            await delay(CHUNK_RETRY_DELAY_MS, controller.signal);
          }
        }

        const uploaded = i + 1;
        patchEntry(id, {
          chunksUploaded: uploaded,
          progress: Math.round((uploaded / numChunks) * 68),
        });
      }

      if (controller.signal.aborted) return;

      // 3. Complete ──────────────────────────────────────────────────────────
      patchEntry(id, { status: 'completing', progress: 70 });

      try {
        await uploadService.complete(sessionId);
      } catch (err: unknown) {
        const axErr = err as { response?: { status?: number; data?: { detail?: string } } };
        if (axErr.response?.status === 409) {
          patchEntry(id, {
            status: 'error',
            progress: 0,
            error: 'Incomplete upload: ' + (axErr.response.data?.detail ?? 'missing chunks'),
          });
          return;
        }
        throw err;
      }

      // 4. Hand off to AI processing.
      //    The queue-driving effect below detects no active upload and
      //    immediately starts the next pending file in parallel.
      patchEntry(id, { status: 'processing', progress: 71, processingStatus: 'pending_cu' });

      pollStatus(id, sessionId);
    } catch (err: unknown) {
      const name = (err as { name?: string }).name;
      if (name === 'AbortError' || name === 'CanceledError') return;
      console.error('[UploadModal] upload failed for', id, err);
      patchEntry(id, { status: 'error', progress: 0, error: 'Upload failed. Please try again.' });
    }
  }, [patchEntry, pollStatus]);

  // ── Queue driver: start next pending file whenever no upload is active ───
  useEffect(() => {
    if (view !== 'queue') return;
    const hasActive = entries.some(
      e => e.status === 'uploading' || e.status === 'completing',
    );
    if (hasActive) return;
    const next = entries.find(e => e.status === 'pending');
    if (next) doUpload(next);
  }, [entries, view, doUpload]);

  // ── Accept multiple files, flag name collisions for resolution ──────────
  const acceptFiles = useCallback((files: File[]) => {
    const videos = files.filter(isVideoFile);
    const rejected = files.length - videos.length;
    if (rejected > 0) {
      dispatch(
        showToast({
          title: 'Unsupported File Type',
          message: `${rejected} file${rejected !== 1 ? 's' : ''} skipped. Only video files (MP4, MOV) are supported.`,
        }),
      );
    }

    const existingNames = new Set(entries.map(e => e.file.name.toLowerCase()));
    const unique: File[] = [];
    const duplicates: DuplicateConflict[] = [];
    const empty: File[] = [];

    for (const f of videos) {
      if (f.size === 0) {
        empty.push(f);
      } else if (existingNames.has(f.name.toLowerCase())) {
        duplicates.push({ id: uid(), file: f });
      } else {
        existingNames.add(f.name.toLowerCase());
        unique.push(f);
      }
    }

    if (unique.length > 0) {
      setEntries(prev => [...prev, ...unique.map(makeFileEntry)]);
      setView('meta');
    }
    if (duplicates.length > 0) {
      setDuplicateQueue(prev => [...prev, ...duplicates]);
    }
    if (empty.length > 0) {
      dispatch(
        showToast({
          title: 'Empty file skipped',
          message: empty.length === 1
            ? `<b style="color:var(--color-text)">${empty[0].name}</b> is empty (0 bytes) and cannot be uploaded.`
            : `${empty.length} files are empty (0 bytes) and cannot be uploaded.`,
        }),
      );
    }
  }, [entries, dispatch]);

  // ── Resolve the duplicate-filename conflict at the front of the queue ───
  const resolveDuplicate = useCallback((action: 'keep' | 'replace' | 'skip') => {
    setDuplicateQueue(prev => {
      const [current, ...rest] = prev;
      if (!current) return prev;

      if (action === 'replace') {
        setEntries(es => [
          ...es.filter(e => e.file.name.toLowerCase() !== current.file.name.toLowerCase()),
          makeFileEntry(current.file),
        ]);
        setView('meta');
      } else if (action === 'keep') {
        setEntries(es => {
          const existingNames = new Set(es.map(e => e.file.name.toLowerCase()));
          const newName = uniqueFileName(current.file.name, existingNames);
          return [...es, makeFileEntry(renameFile(current.file, newName))];
        });
        setView('meta');
      }
      // 'skip' — discard the duplicate, nothing to add

      return rest;
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) acceptFiles(files);
  }, [acceptFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      acceptFiles(files);
      e.target.value = ''; // reset so same files can be re-selected
    }
  };

  // ── Start the queue ──────────────────────────────────────────────────────
  const startQueue = useCallback(() => {
    if (entries.length === 0) return;
    dispatch(setUploadInProgress(true));
    setView('queue');
    // The queue-driver effect above picks up the first pending entry automatically.
  }, [entries, dispatch]);

  // ── Cancel all uploads and close ─────────────────────────────────────────
  const handleCancelAll = useCallback(() => {
    const hasActive = entries.some(
      e => e.status === 'uploading' || e.status === 'completing' || e.status === 'processing',
    );
    if (hasActive && !window.confirm('Cancel all uploads? Files still uploading or being analysed will be stopped.')) {
      return;
    }
    abortRefs.current.forEach(c => c.abort());
    pollRefs.current.forEach(t => clearTimeout(t));
    dispatch(setUploadInProgress(false));
    dispatch(closeUploadModal());
  }, [entries, dispatch]);

  // ── Computed stats ───────────────────────────────────────────────────────
  const totalFiles  = entries.length;
  const doneFiles   = entries.filter(e => e.status === 'done').length;
  const overallPct  = totalFiles > 0
    ? Math.round(entries.reduce((sum, e) => sum + e.progress, 0) / totalFiles)
    : 0;
  const currentStepIdx = view === 'drop' ? 0 : view === 'meta' ? 1 : 2;

  // ── Minimized floating widget ────────────────────────────────────────────
  if (minimized && view === 'queue') {
    return (
      <div className={styles.minimizedContainer}>
        <div
          className={styles.minimizedCard}
          onClick={() => setMinimized(false)}
          role="button"
          tabIndex={0}
          aria-label="Expand upload panel"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMinimized(false); }}
        >
          <span className={styles.miniSpinner} aria-hidden="true" />
          <div className={styles.miniInfo}>
            <div className={styles.miniTitle}>
              Uploading {doneFiles} / {totalFiles} files
            </div>
            <div className={styles.miniProgressTrack}>
              <div className={styles.miniProgressFill} style={{ width: overallPct + '%' }} />
            </div>
          </div>
          <button
            className={styles.miniExpandBtn}
            onClick={(e) => { e.stopPropagation(); setMinimized(false); }}
            aria-label="Expand"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M2 8l4-4 4 4" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  const currentDuplicate = duplicateQueue[0];

  // ── Full modal ───────────────────────────────────────────────────────────
  return (
    <>
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget && view !== 'queue') handleClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Upload content"
    >
      <div className={styles.modal}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div>
              <div className={styles.headerTitle}>
                {view === 'queue'
                  ? 'Uploading ' + doneFiles + ' / ' + totalFiles + ' files'
                  : 'Upload Content'}
              </div>
              <div className={styles.headerSub}>
                {view === 'queue'
                  ? 'AI indexing in progress — you can keep working'
                  : 'Video & documents — indexed with AI'}
              </div>
            </div>
          </div>

          <div className={styles.headerActions}>
            {view === 'queue' && (
              <button
                className={styles.minimizeBtn}
                onClick={() => setMinimized(true)}
                aria-label="Minimise upload panel"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M2 5l5 5 5-5" />
                </svg>
              </button>
            )}
            <button
              className={styles.closeBtn}
              onClick={view === 'queue' ? handleCancelAll : handleClose}
              aria-label={view === 'queue' ? 'Cancel all uploads and close' : 'Close upload modal'}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M1 1l12 12M13 1L1 13" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Step bar ── */}
        <div className={styles.stepBar} aria-label="Upload steps">
          {VIEW_STEPS.map((label, i) => (
            <div key={label} className={styles.stepItem}>
              <div
                className={[
                  styles.stepDot,
                  currentStepIdx === i ? styles.stepDotActive : '',
                  currentStepIdx > i  ? styles.stepDotDone   : '',
                ].join(' ')}
                aria-current={currentStepIdx === i ? 'step' : undefined}
              >
                {currentStepIdx > i ? (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M1.5 5l2.5 2.5 4.5-4.5" />
                  </svg>
                ) : (i + 1)}
              </div>
              <span className={[styles.stepLabel, currentStepIdx === i ? styles.stepLabelActive : ''].join(' ')}>
                {label}
              </span>
              {i < VIEW_STEPS.length - 1 && (
                <div className={[styles.stepConnector, currentStepIdx > i ? styles.stepConnectorDone : ''].join(' ')} />
              )}
            </div>
          ))}
        </div>

        {/* ───────────────────────────────────────────────────────────── */}
        {/* Step 1 — Drop zone                                           */}
        {/* ───────────────────────────────────────────────────────────── */}
        {view === 'drop' && (
          <div className={styles.body}>
            <div
              className={[styles.dropzone, dragOver ? styles.dropzoneActive : ''].join(' ')}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Drop files or click to browse"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="video/mp4,video/quicktime,.mov,.MOV,.mp4"
                className={styles.fileInput}
                onChange={handleFileInput}
              />
              <div className={[styles.dropIconWrap, dragOver ? styles.dropIconWrapActive : ''].join(' ')}>
                {dragOver ? (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v12M8 11l4 4 4-4" />
                    <path d="M4 19h16" />
                  </svg>
                ) : (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                )}
              </div>
              <div className={styles.dropTitle}>
                {dragOver ? 'Release to add files' : 'Drop files here'}
              </div>
              <div className={styles.dropSub}>or click to browse — select multiple files at once</div>
              <div className={styles.fmtRow}>
                {['MP4', 'MOV'].map(f => (
                  <span key={f} className={styles.fmtBadge}>{f}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ───────────────────────────────────────────────────────────── */}
        {/* Step 2 — Metadata per file                                   */}
        {/* ───────────────────────────────────────────────────────────── */}
        {view === 'meta' && (
          <div className={styles.body}>
            {/* Hidden input for "+ Add more" */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="video/mp4,video/quicktime,.mov,.MOV,.mp4"
              className={styles.fileInput}
              onChange={handleFileInput}
            />

            <div className={styles.metaHeader}>
              <span className={styles.metaCount}>
                {entries.length} file{entries.length !== 1 ? 's' : ''} selected
              </span>
              <button className={styles.addMoreBtn} onClick={() => fileInputRef.current?.click()}>
                + Add more
              </button>
            </div>

            <div className={styles.metaList}>
              {entries.map((entry) => {
                const ext = getExt(entry.file);
                return (
                  <div key={entry.id} className={styles.metaEntry}>
                    {/* File chip row */}
                    <div className={styles.metaEntryHeader}>
                      <span className={[styles.fileExt, styles['ext' + ext] ?? ''].join(' ')}>{ext}</span>
                      <div className={styles.fileInfo}>
                        <div className={styles.fileName}>{entry.file.name}</div>
                        <div className={styles.fileSize}>{formatBytes(entry.file.size)}</div>
                      </div>
                      <button
                        className={styles.removeBtn}
                        onClick={() => setEntries(prev => prev.filter(e => e.id !== entry.id))}
                        aria-label={'Remove ' + entry.file.name}
                      >
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M1 1l12 12M13 1L1 13" />
                        </svg>
                      </button>
                    </div>

                    {/* Title input */}
                    <input
                      className={styles.input}
                      value={entry.title}
                      onChange={(e) =>
                        setEntries(prev =>
                          prev.map(en => en.id === entry.id ? { ...en, title: e.target.value } : en),
                        )
                      }
                      placeholder="Enter a title..."
                      disabled
                    />

                    {/* Content type toggle */}
                    <div className={styles.typeToggle} role="group" aria-label="Content type">
                      <button
                        className={[styles.typeBtn, entry.contentType === 'media' ? styles.typeBtnActive : ''].join(' ')}
                        onClick={() =>
                          setEntries(prev =>
                            prev.map(en => en.id === entry.id ? { ...en, contentType: 'media' } : en),
                          )
                        }
                        type="button"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        Media
                      </button>
                      <button
                        className={[styles.typeBtn, styles.typeBtnDisabled].join(' ')}
                        type="button"
                        disabled
                        title="Document upload coming soon"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        Document
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.metaActions}>
              <Button variant="ghost" size="sm" onClick={() => { setEntries([]); setView('drop'); }}>
                ← Back
              </Button>
              <Button
                variant="gradient"
                size="sm"
                onClick={startQueue}
                disabled={entries.length === 0 || entries.some(e => !e.title.trim())}
                className={styles.btnGreen}
              >
                Start {entries.length > 1 ? entries.length + ' Uploads' : 'Upload'} →
              </Button>
            </div>
          </div>
        )}

        {/* ───────────────────────────────────────────────────────────── */}
        {/* Step 3 — Upload queue                                        */}
        {/* ───────────────────────────────────────────────────────────── */}
        {view === 'queue' && (
          <div className={styles.body}>
            {/* Overall progress */}
            <div className={styles.queueSummary}>
              <div className={styles.queueProgressTrack}>
                <div className={styles.queueProgressFill} style={{ width: overallPct + '%' }} />
              </div>
              <div className={styles.queueSummaryRow}>
                <span className={styles.queueSummaryLabel}>
                  {doneFiles === totalFiles
                    ? 'All files indexed!'
                    : doneFiles + ' of ' + totalFiles + ' complete'}
                </span>
                <span className={styles.queueSummaryPct}>{overallPct}%</span>
              </div>
            </div>

            {/* Per-file rows */}
            <div className={styles.queueList}>
              {entries.map((entry) => {
                const ext       = getExt(entry.file);
                const isActive  = entry.status === 'uploading' || entry.status === 'completing';
                const isProc    = entry.status === 'processing';
                const isDone    = entry.status === 'done';
                const isError   = entry.status === 'error';
                const isPending = entry.status === 'pending';

                return (
                  <div
                    key={entry.id}
                    className={[
                      styles.queueEntry,
                      isActive ? styles.queueEntryActive    : '',
                      isProc   ? styles.queueEntryProcessing : '',
                      isDone   ? styles.queueEntryDone       : '',
                      isError  ? styles.queueEntryError      : '',
                    ].join(' ')}
                  >
                    {/* File type badge */}
                    <div className={styles.queueEntryLeft}>
                      <span className={[styles.queueExt, styles['ext' + ext] ?? ''].join(' ')}>
                        {ext}
                      </span>
                    </div>

                    {/* Main content */}
                    <div className={styles.queueEntryMain}>
                      <div className={styles.queueEntryName}>{entry.title || entry.file.name}</div>

                      {/* Per-file mini progress bar */}
                      {(isActive || isProc) && (
                        <div className={styles.queueMiniTrack}>
                          <div className={styles.queueMiniFill} style={{ width: entry.progress + '%' }} />
                        </div>
                      )}

                      {/* Status line */}
                      <div className={styles.queueEntryStatus}>
                        {isPending && (
                          <span className={styles.statusPending}>Queued</span>
                        )}
                        {isActive && (
                          <span className={styles.statusUploading}>
                            {entry.status === 'completing'
                              ? 'Assembling on server...'
                              : Math.round(entry.progress) + '%'}
                          </span>
                        )}
                        {isProc && entry.processingStatus && (
                          <span className={styles.statusProcessing}>
                            {PROCESSING_LABELS[entry.processingStatus]}
                          </span>
                        )}
                        {isDone  && <span className={styles.statusDone}>✓ Indexed</span>}
                        {isError && <span className={styles.statusError}>✗ {entry.error}</span>}
                      </div>
                    </div>

                    {/* Right icon / cancel */}
                    <div className={styles.queueEntryRight}>
                      {isDone && (
                        <span className={styles.doneCheck} aria-label="Done">
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M1.5 6l3 3 6-6" />
                          </svg>
                        </span>
                      )}
                      {isProc && (
                        <span className={styles.entrySpinner} aria-hidden="true" />
                      )}
                      {(isPending || isActive) && (
                        <button
                          className={styles.cancelEntryBtn}
                          aria-label={'Cancel ' + (entry.title || entry.file.name)}
                          onClick={() => {
                            abortRefs.current.get(entry.id)?.abort();
                            startedIds.current.delete(entry.id);
                            setEntries(prev => prev.filter(e => e.id !== entry.id));
                          }}
                        >
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M1 1l8 8M9 1L1 9" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>

    {/* ── Duplicate filename conflict ── */}
    {currentDuplicate && (
      <div className={styles.conflictBackdrop} role="dialog" aria-modal="true" aria-label="Duplicate file name">
        <div className={styles.conflictDialog}>
          <p className={styles.conflictTitle}>Duplicate file name</p>
          <p className={styles.conflictMessage}>
            A file named <strong>{currentDuplicate.file.name}</strong> is already in this upload.
            {duplicateQueue.length > 1 && ` (${duplicateQueue.length - 1} more duplicate${duplicateQueue.length > 2 ? 's' : ''} to resolve)`}
          </p>
          <div className={styles.conflictActions}>
            <button className={styles.conflictBtn} onClick={() => resolveDuplicate('skip')}>
              Skip
            </button>
            <button className={styles.conflictBtn} onClick={() => resolveDuplicate('replace')}>
              Replace
            </button>
            <button className={[styles.conflictBtn, styles.conflictBtnPrimary].join(' ')} onClick={() => resolveDuplicate('keep')}>
              Keep both
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
